import { Key } from "react"
import { SupportedTokenName } from "@mezo/sdk"
import { FlexGrid, FlexGridItem, useStyletron } from "@mezo-org/mezo-clay"

import {
  DepositWithWithdrawalStatus,
  selectDepositsWithWithdrawalStatus,
  useDappSelector,
} from "#/redux-state"
import {
  DEPOSIT_BOX_FRIENDLY_DECIMALS_AMOUNT,
  IS_MEZO_MAINNET,
} from "#/shared/constants"
import { DepositPageTab } from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  getDepositMigrationStatus,
  getUnlockTime,
  sortDepositsByLockEnd,
} from "#/shared/utils"

import DepositBox from "./DepositBox"
import DepositButton from "./DepositButton"

type DepositListProps = {
  setActiveTab: (key: Key) => void
}

export default function DepositList({ setActiveTab }: DepositListProps) {
  const deposits = useDappSelector(selectDepositsWithWithdrawalStatus)

  const [, theme] = useStyletron()

  if (!deposits || !deposits.length) return null

  const awaitingDeposits = sortDepositsByLockEnd(
    deposits.filter((deposit) => !deposit.depositId),
    "desc",
  )

  const completedDeposits = [...deposits]
    .filter((deposit) => !!deposit.depositId && !deposit.withdrawn)
    .sort((a, b) => b.depositId - a.depositId)

  const withdrawnDeposits = [...deposits].filter((deposit) => deposit.withdrawn)

  const sortedDeposits = [
    ...awaitingDeposits,
    ...completedDeposits,
    ...withdrawnDeposits,
  ] as DepositWithWithdrawalStatus[]

  return (
    <FlexGrid
      flexGridColumnCount={[1, 1, 1, 2]}
      flexGridColumnGap={theme.sizing.scale900}
      flexGridRowGap={theme.sizing.scale900}
    >
      {sortedDeposits.map(
        ({
          id,
          depositId,
          amount,
          token,
          locks,
          withdrawn,
          redemptionNeeded,
          dailyMats,
          totalDailyMats,
          dailyBoostMats,
          mintedAmount,
          migration,
        }) => {
          if (withdrawn && !redemptionNeeded) return null

          const currentLock = locks[locks.length - 1]
          const { lockStart, lockEnd } = currentLock

          const unlockTime = getUnlockTime(new Date(lockEnd).getTime())
          const bigIntAmount = BigInt(amount)

          // We should always assume amount here will have 18 decimals because this value
          // comes from the API and it is already converted to common decimals
          const userAmount = bigIntToHumanReadableFormat(
            bigIntAmount,
            18,
            DEPOSIT_BOX_FRIENDLY_DECIMALS_AMOUNT,
          )

          const nonZeroDailyBoostMats = dailyBoostMats.filter(
            (dailyBoostMat) => dailyBoostMat.value > 0,
          )

          const migrationStatus = getDepositMigrationStatus(token, migration)

          return (
            <FlexGridItem key={id}>
              <DepositBox
                id={id}
                depositId={depositId}
                userAmount={userAmount}
                amount={bigIntAmount}
                dailyMats={dailyMats}
                totalDailyMats={totalDailyMats}
                lockStart={new Date(lockStart)}
                lockEnd={new Date(lockEnd)}
                token={token as SupportedTokenName}
                unlockTime={unlockTime}
                dailyBoostMats={nonZeroDailyBoostMats}
                mintedAmount={BigInt(mintedAmount)}
                migrationStatus={migrationStatus}
              />
            </FlexGridItem>
          )
        },
      )}
      {!IS_MEZO_MAINNET && (
        <FlexGridItem>
          <DepositButton
            onClick={() => setActiveTab(DepositPageTab.NEW_DEPOSIT)}
          />
        </FlexGridItem>
      )}
    </FlexGrid>
  )
}
